import React, { useState, useEffect } from 'react';
import { API_URL } from '../config';
import { apiFetch, getAuthHeaders } from '../api';
import './Report.css';

interface ReportProps {
  siret: string | null;
  reportId: string | null;
  onBack: () => void;
}

function Report({ siret, reportId, onBack }: ReportProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState<any>(null);
  const [progress, setProgress] = useState(0);
  const [companyName, setCompanyName] = useState('');
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [currentPdfUrl, setCurrentPdfUrl] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);
  const [loadingDocumentId, setLoadingDocumentId] = useState<string | null>(null);
  const [activeDocTab, setActiveDocTab] = useState('procedures');
  const [companyInfo, setCompanyInfo] = useState<any>(null);
  const [showIAModal, setShowIAModal] = useState(false);

  useEffect(() => {
    loadReport();
  }, [siret, reportId]);

  const loadCompanyInfo = async (siretNumber: string) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/company-info/${siretNumber}`);
      const result = await response.json();
      if (result.success) {
        setCompanyInfo(result.data);
      }
    } catch (err) {
      console.error('Erreur lors du chargement des infos entreprise:', err);
    }
  };

  const loadReport = async () => {
    setLoading(true);
    setError('');
    setProgress(0);

    if (reportId) {
      try {
        const response = await apiFetch(`/api/solvability/report/${reportId}`);
        const result = await response.json();

        if (result.success) {
          setData(result.data);
          setCompanyName(result.data.company_info?.company_name || 'Entreprise');
          // Charger les infos enrichies
          if (result.data.siret) {
            await loadCompanyInfo(result.data.siret);
          }
          setLoading(false);
        } else {
          setError(result.message || 'Erreur lors du chargement du rapport');
          setLoading(false);
        }
      } catch (err: any) {
        setError('Erreur de connexion: ' + err.message);
        setLoading(false);
      }
      return;
    }

    if (!siret) return;

    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 90) return prev;
        return prev + Math.random() * 15;
      });
    }, 300);

    try {
      // Charger les infos enrichies en parallèle
      const companyInfoPromise = loadCompanyInfo(siret);

      const response = await apiFetch(`/api/solvability/check/${siret}`);
      const result = await response.json();

      clearInterval(progressInterval);
      setProgress(100);

      if (result.success) {
        setData(result.data);
        setCompanyName(result.data.company_info?.company_name || 'Entreprise');
        // Attendre les infos enrichies
        await companyInfoPromise;
        // L'historique est géré par MongoDB via le backend
      } else {
        setError(result.message || 'Erreur lors du chargement du rapport');
      }
    } catch (err: any) {
      clearInterval(progressInterval);
      setError('Erreur de connexion: ' + err.message);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 15) return '#4caf50';
    if (score >= 10) return '#ff9800';
    if (score >= 5) return '#ff5722';
    return '#f44336';
  };

  const getRiskBadge = (risk: string) => {
    const badges: any = {
      low: { label: 'Faible', color: '#4caf50' },
      medium: { label: 'Moyen', color: '#ff9800' },
      high: { label: 'Élevé', color: '#ff5722' },
      very_high: { label: 'Très élevé', color: '#f44336' },
      critical: { label: 'Critique', color: '#d32f2f' },
      unknown: { label: 'Inconnu', color: '#999' }
    };
    return badges[risk] || badges.unknown;
  };

  const viewReport = () => {
    setLoading(false);
  };

  const openPdfModal = async (documentId: string) => {
    setLoadingDocumentId(documentId);
    setPdfLoading(true);

    const pdfUrl = `${API_URL}/api/solvability/document/${documentId}`;

    try {
      // Vérifier que le PDF est accessible avant d'ouvrir le modal
      const response = await fetch(pdfUrl, { method: 'HEAD', headers: getAuthHeaders() });

      if (response.ok) {
        setCurrentPdfUrl(pdfUrl);
        setPdfModalOpen(true);
      } else if (response.status === 404) {
        alert('Ce document n\'est pas disponible sur le serveur BODACC. Le PDF n\'a peut-être pas été généré pour cette annonce.');
      } else {
        alert('Erreur lors du chargement du document');
      }
    } catch (err) {
      alert('Erreur lors du chargement du document. Le PDF peut ne pas être disponible sur le serveur BODACC.');
    } finally {
      setPdfLoading(false);
      setLoadingDocumentId(null);
    }
  };

  const closePdfModal = () => {
    setPdfModalOpen(false);
    setCurrentPdfUrl('');
  };

  const downloadPdf = () => {
    if (currentPdfUrl) {
      window.open(currentPdfUrl + '?download=true', '_blank');
    }
  };

  if (loading && progress < 100) {
    return (
      <div className="report">
        <button onClick={onBack} className="btn-back">← Retour</button>
        <div className="loading-progress">
          <div className="progress-info">
            <span className="progress-company">{companyName || 'Analyse en cours...'}</span>
            <span className="progress-percent">{Math.round(progress)}%</span>
          </div>
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progress}%` }}></div>
          </div>
        </div>
      </div>
    );
  }

  if (loading && progress === 100 && data) {
    return (
      <div className="report">
        <button onClick={onBack} className="btn-back">← Retour</button>
        <div className="loading-progress">
          <div className="progress-info">
            <span className="progress-company">{companyName}</span>
            <span className="progress-score">Score: {data.financial_score}/20</span>
          </div>
          <div className="progress-bar">
            <div className="progress-fill complete" style={{ width: '100%' }}></div>
          </div>
          <button onClick={viewReport} className="btn-view-full-report">
            Consulter le rapport
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="report">
        <button onClick={onBack} className="btn-back">← Retour</button>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  const riskBadge = getRiskBadge(data.risk_level);

  return (
    <div className="report">
      <button onClick={onBack} className="btn-back">← Retour</button>

      <div className="report-header">
        <div className="report-header-main">
          <h1>{companyInfo?.nom || data.company_info?.company_name || 'Rapport de solvabilité'}</h1>
          {companyInfo?.statut && (
            <span className="company-status">{companyInfo.statut}</span>
          )}
        </div>

        <div className="report-info-grid">
          <div className="report-info-item">
            <svg className="info-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
              <circle cx="12" cy="10" r="3"></circle>
            </svg>
            <div className="info-content">
              <span className="info-label">Adresse</span>
              <span className="info-value">{companyInfo?.adresse || 'Non disponible'}</span>
            </div>
          </div>

          <div className="report-info-item">
            <svg className="info-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
            <div className="info-content">
              <span className="info-label">Dirigeant(s)</span>
              <span className="info-value">{companyInfo?.dirigeants || 'Non disponible'}</span>
            </div>
          </div>

          <div className="report-info-item">
            <svg className="info-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
            <div className="info-content">
              <span className="info-label">Date de création</span>
              <span className="info-value">{companyInfo?.date_creation || 'Non disponible'}</span>
            </div>
          </div>

          <div className="report-info-item">
            <svg className="info-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10 9 9 9 8 9"></polyline>
            </svg>
            <div className="info-content">
              <span className="info-label">Forme juridique</span>
              <span className="info-value">{companyInfo?.forme_juridique || 'Non disponible'}</span>
            </div>
          </div>

          <div className="report-info-item">
            <svg className="info-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
              <line x1="1" y1="10" x2="23" y2="10"></line>
            </svg>
            <div className="info-content">
              <span className="info-label">SIRET / SIREN</span>
              <span className="info-value">{data.siret} / {companyInfo?.siren || data.siret.substring(0, 9)}</span>
            </div>
          </div>

          <div className="report-info-item">
            <svg className="info-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="2" y1="12" x2="22" y2="12"></line>
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
            </svg>
            <div className="info-content">
              <span className="info-label">N° TVA</span>
              <span className="info-value">{companyInfo?.tva || 'Non disponible'}</span>
            </div>
          </div>

          <div className="report-info-item">
            <svg className="info-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
            <div className="info-content">
              <span className="info-label">Activité</span>
              <span className="info-value">{companyInfo?.activite || 'Non disponible'}</span>
            </div>
          </div>

          <div className="report-info-item">
            <svg className="info-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
            <div className="info-content">
              <span className="info-label">Effectif</span>
              <span className="info-value">{companyInfo?.effectif || 'Non précisé'}</span>
            </div>
          </div>

          <div className="report-info-item">
            <svg className="info-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
            <div className="info-content">
              <span className="info-label">Vérifié le</span>
              <span className="info-value">{new Date(data.checked_at).toLocaleString('fr-FR')}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="ia-cta-banner" onClick={() => setShowIAModal(true)}>
        <div className="ia-cta-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
            <path d="M2 17l10 5 10-5"></path>
            <path d="M2 12l10 5 10-5"></path>
          </svg>
        </div>
        <div className="ia-cta-content">
          <h3>Analyse IA approfondie</h3>
          <p>Obtenez une analyse détaillée alimentée par l'intelligence artificielle avec des recommandations personnalisées</p>
        </div>
        <div className="ia-cta-action">
          <button className="ia-cta-btn">
            Découvrir
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14"></path>
              <path d="M12 5l7 7-7 7"></path>
            </svg>
          </button>
        </div>
      </div>

      {showIAModal && (
        <div className="modal-overlay" onClick={() => setShowIAModal(false)}>
          <div className="ia-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowIAModal(false)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
            <div className="ia-modal-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                <path d="M2 17l10 5 10-5"></path>
                <path d="M2 12l10 5 10-5"></path>
              </svg>
            </div>
            <h2>Analyse IA approfondie</h2>
            <p className="ia-modal-description">
              Accédez à une analyse approfondie de cette entreprise alimentée par l'intelligence artificielle.
              Obtenez des insights exclusifs, des prédictions de risque avancées et des recommandations personnalisées.
            </p>
            <div className="ia-modal-features">
              <div className="ia-feature">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                <span>Analyse prédictive du risque</span>
              </div>
              <div className="ia-feature">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                <span>Détection d'anomalies financières</span>
              </div>
              <div className="ia-feature">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                <span>Recommandations stratégiques</span>
              </div>
              <div className="ia-feature">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                <span>Alertes personnalisées</span>
              </div>
            </div>
            <div className="ia-modal-upgrade">
              <p className="upgrade-text">Cette fonctionnalité nécessite un abonnement Premium</p>
              <button className="upgrade-btn" onClick={() => window.location.href = '/abonnement'}>
                Passer à Premium
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="report-summary">
        <div className="summary-card score-card">
          <h3>Score de solvabilité</h3>
          <div className="score-circle" style={{ borderColor: getScoreColor(data.financial_score) }}>
            <span className="score-value" style={{ color: getScoreColor(data.financial_score) }}>
              {data.financial_score}/20
            </span>
          </div>
        </div>

        <div className="summary-card risk-card">
          <h3>Niveau de risque</h3>
          <div className="risk-badge-large" style={{ background: riskBadge.color }}>
            {riskBadge.label}
          </div>
          <p className="risk-description">
            {data.financial_health === 'excellent' && 'Situation financière excellente'}
            {data.financial_health === 'good' && 'Bonne santé financière'}
            {data.financial_health === 'average' && 'Situation financière moyenne'}
            {data.financial_health === 'poor' && 'Situation financière préoccupante'}
            {data.financial_health === 'critical' && 'Situation critique'}
          </p>
        </div>
      </div>

      {data.financial_analysis?.score_explanation && (
        <div className="report-section">
          <h2>Explication du score</h2>
          <div className="score-explanation">
            {data.financial_analysis.score_explanation.interpretation?.map((interp: string, idx: number) => (
              <p key={idx} className="interpretation-text">{interp}</p>
            ))}

            <div className="score-breakdown">
              <h3>Composition du score</h3>
              <div className="breakdown-items">
                {data.financial_analysis.score_explanation.breakdown.items && data.financial_analysis.score_explanation.breakdown.items.length > 0 ? (
                  <>
                    {data.financial_analysis.score_explanation.breakdown.items.map((item: any, idx: number) => (
                      <div key={idx} className="breakdown-item">
                        <span className="breakdown-label">{item.label}</span>
                        <span className={`breakdown-value ${item.points >= 0 ? 'positive' : 'negative'}`}>
                          {item.points > 0 ? '+' : ''}{item.points} points
                        </span>
                      </div>
                    ))}
                  </>
                ) : (
                  <>
                    <div className="breakdown-item">
                      <span className="breakdown-label">Ancienneté de l'entreprise</span>
                      <span className={`breakdown-value ${data.financial_analysis.score_explanation.breakdown.age_company >= 0 ? 'positive' : 'negative'}`}>
                        {data.financial_analysis.score_explanation.breakdown.age_company > 0 ? '+' : ''}{data.financial_analysis.score_explanation.breakdown.age_company} points
                      </span>
                    </div>

                    <div className="breakdown-item">
                      <span className="breakdown-label">Statut administratif</span>
                      <span className={`breakdown-value ${data.financial_analysis.score_explanation.breakdown.status >= 0 ? 'positive' : 'negative'}`}>
                        {data.financial_analysis.score_explanation.breakdown.status > 0 ? '+' : ''}{data.financial_analysis.score_explanation.breakdown.status} points
                      </span>
                    </div>

                    {data.financial_analysis.score_explanation.breakdown.financial_health !== 0 && (
                      <div className="breakdown-item">
                        <span className="breakdown-label">Santé financière</span>
                        <span className={`breakdown-value ${data.financial_analysis.score_explanation.breakdown.financial_health >= 0 ? 'positive' : 'negative'}`}>
                          {data.financial_analysis.score_explanation.breakdown.financial_health > 0 ? '+' : ''}{data.financial_analysis.score_explanation.breakdown.financial_health} points
                        </span>
                      </div>
                    )}

                    {data.financial_analysis.score_explanation.breakdown.judicial_risks && data.financial_analysis.score_explanation.breakdown.judicial_risks.length > 0 && (
                      <>
                        {data.financial_analysis.score_explanation.breakdown.judicial_risks.map((risk: any, idx: number) => (
                          <div key={idx} className="breakdown-item">
                            <span className="breakdown-label">{risk.label}</span>
                            <span className={`breakdown-value ${risk.points >= 0 ? 'positive' : 'negative'}`}>
                              {risk.points} points
                            </span>
                          </div>
                        ))}
                      </>
                    )}

                    {data.financial_analysis.score_explanation.breakdown.judicial_risk !== undefined && data.financial_analysis.score_explanation.breakdown.judicial_risk !== 0 && !data.financial_analysis.score_explanation.breakdown.judicial_risks && (
                      <div className="breakdown-item">
                        <span className="breakdown-label">Risque judiciaire</span>
                        <span className={`breakdown-value ${data.financial_analysis.score_explanation.breakdown.judicial_risk >= 0 ? 'positive' : 'negative'}`}>
                          {data.financial_analysis.score_explanation.breakdown.judicial_risk} points
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="score-total">
                <span className="total-label">Total</span>
                <span className="total-value">
                  {(() => {
                    let total = 0;

                    if (data.financial_analysis.score_explanation.breakdown.items) {
                      data.financial_analysis.score_explanation.breakdown.items.forEach((item: any) => {
                        total += item.points;
                      });
                    } else {
                      total = data.financial_analysis.score_explanation.breakdown.age_company || 0;
                      total += data.financial_analysis.score_explanation.breakdown.status || 0;
                      total += data.financial_analysis.score_explanation.breakdown.financial_health || 0;

                      if (data.financial_analysis.score_explanation.breakdown.judicial_risks) {
                        data.financial_analysis.score_explanation.breakdown.judicial_risks.forEach((risk: any) => {
                          total += risk.points;
                        });
                      }
                    }

                    return total;
                  })()} points
                </span>
              </div>

              <div className="score-final score-final-large">
                <span className="final-label">Score</span>
                <span className="final-value">{data.financial_analysis.score_explanation.normalized_score}/100</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {data.risks && data.risks.length > 0 && (
        <div className="report-section alert-section">
          <h2>Risques</h2>
          <div className="recommendations-grid">
            {data.risks.map((risk: any, idx: number) => {
              const riskText = typeof risk === 'string' ? risk : risk.text;
              const riskDocId = typeof risk === 'object' ? risk.document_id : null;
              const procedureStatus = typeof risk === 'object' ? risk.procedure_status : null;

              return (
                <div key={idx} className="recommendation-item recommendation-negative">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '15px' }}>
                    <div style={{ flex: 1 }}>
                      {riskText}
                      {procedureStatus && (
                        <div style={{ fontSize: '0.85rem', marginTop: '5px', color: '#999' }}>
                          Statut: {procedureStatus}
                        </div>
                      )}
                    </div>
                    {riskDocId && (
                      <button
                        onClick={() => openPdfModal(riskDocId)}
                        className="btn-pdf"
                        disabled={loadingDocumentId === riskDocId}
                        style={{ marginTop: 0, fontSize: '0.8rem', padding: '6px 12px' }}
                      >
                        {loadingDocumentId === riskDocId ? 'Chargement...' : 'Consulter'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {data.vigilance && data.vigilance.length > 0 && (
        <div className="report-section">
          <h2>Vigilance</h2>
          <div className="recommendations-grid">
            {data.vigilance.map((item: string, idx: number) => (
              <div key={idx} className="recommendation-item recommendation-warning">
                {item}
              </div>
            ))}
          </div>
        </div>
      )}

      {data.informations && data.informations.length > 0 && (
        <div className="report-section">
          <h2>Informations</h2>
          <div className="recommendations-grid">
            {data.informations.map((info: string, idx: number) => {
              const isPositive = info.includes('Excellente') ||
                                 info.includes('Bonne') ||
                                 info.includes('active') ||
                                 info.includes('solide') ||
                                 info.includes('croissance') ||
                                 info.includes('positive');

              let className = 'recommendation-item';
              if (isPositive) className += ' recommendation-positive';
              else className += ' recommendation-neutral';

              return (
                <div key={idx} className={className}>
                  {info}
                </div>
              );
            })}
          </div>
        </div>
      )}


      {data.bodacc_data && data.bodacc_data.all_announcements && data.bodacc_data.all_announcements.length > 0 && (
        <div className="report-section">
          <h2>Documents</h2>

          {(() => {
            const procedures = data.bodacc_data.all_announcements.filter((a: any) =>
              a.category === 'Liquidation judiciaire' ||
              a.category === 'Redressement judiciaire' ||
              a.category === 'Plan de redressement' ||
              a.category === 'Procédure collective'
            );
            const comptes = data.bodacc_data.all_announcements.filter((a: any) => a.category === 'Comptes annuels');
            const modifications = data.bodacc_data.all_announcements.filter((a: any) => a.category === 'Modification');
            const autres = data.bodacc_data.all_announcements.filter((a: any) =>
              a.category !== 'Liquidation judiciaire' &&
              a.category !== 'Redressement judiciaire' &&
              a.category !== 'Plan de redressement' &&
              a.category !== 'Procédure collective' &&
              a.category !== 'Comptes annuels' &&
              a.category !== 'Modification'
            );

            const tabs = [
              { id: 'procedures', label: 'Procédures collectives', count: procedures.length, data: procedures },
              { id: 'comptes', label: 'Dépôts des comptes', count: comptes.length, data: comptes },
              { id: 'modifications', label: 'Modifications diverses', count: modifications.length, data: modifications },
              { id: 'autres', label: 'Autres', count: autres.length, data: autres }
            ].filter(tab => tab.count > 0);

            const renderAnnouncements = (announcements: any[]) => {
              if (announcements.length === 0) {
                return <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>Aucun document dans cette catégorie</div>;
              }

              return (
                <div className="announcements-list">
                  {announcements.map((announcement: any, idx: number) => {
                    const isJudicial = announcement.category === 'Liquidation judiciaire' ||
                                       announcement.category === 'Redressement judiciaire' ||
                                       announcement.category === 'Plan de redressement' ||
                                       announcement.category === 'Procédure collective';

                    return (
                      <div key={idx} className={`announcement-card ${isJudicial ? 'announcement-judicial' : ''}`}>
                        <div className="announcement-header">
                          <div className="announcement-title-row">
                            <span className="announcement-type-badge">{announcement.type}</span>
                            <h4 className="announcement-title">{announcement.title}</h4>
                          </div>
                          {announcement.date && (
                            <span className="announcement-date">{announcement.date}</span>
                          )}
                        </div>
                        {announcement.content && (
                          <div className="announcement-content">{announcement.content}</div>
                        )}
                        {announcement.has_pdf && announcement.document_id && (
                          <div className="announcement-actions">
                            <button
                              onClick={() => openPdfModal(announcement.document_id)}
                              className="btn-pdf"
                              disabled={loadingDocumentId === announcement.document_id}
                            >
                              {loadingDocumentId === announcement.document_id ? 'Chargement...' : 'Consulter'}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            };

            // Définir le premier onglet actif par défaut
            if (tabs.length > 0 && !tabs.find(t => t.id === activeDocTab)) {
              setActiveDocTab(tabs[0].id);
            }

            return (
              <>
                {tabs.length > 0 ? (
                  <>
                    <div className="documents-tabs">
                      {tabs.map(tab => (
                        <button
                          key={tab.id}
                          className={`documents-tab ${activeDocTab === tab.id ? 'active' : ''}`}
                          onClick={() => setActiveDocTab(tab.id)}
                        >
                          {tab.label} ({tab.count})
                        </button>
                      ))}
                    </div>
                    <div className="documents-tab-content">
                      {renderAnnouncements(tabs.find(t => t.id === activeDocTab)?.data || [])}
                    </div>
                  </>
                ) : (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>Aucun document disponible</div>
                )}
              </>
            );
          })()}
        </div>
      )}

      {pdfModalOpen && (
        <div className="pdf-modal-overlay" onClick={closePdfModal}>
          <div className="pdf-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="pdf-modal-header">
              <h3>Document</h3>
              <div className="pdf-modal-actions">
                <button onClick={downloadPdf} className="btn-download">
                  Télécharger
                </button>
                <button onClick={closePdfModal} className="btn-close">
                  ✕
                </button>
              </div>
            </div>
            <div className="pdf-modal-body">
              <iframe
                src={currentPdfUrl}
                title="Document PDF"
                width="100%"
                height="100%"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Report;
